import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, doc, getDocs, setDoc } from 'firebase/firestore';
import { auth, db, callFunction } from '../firebase';
import { useSuperAdminSession } from '../session';
import PulseLogo from '../components/PulseLogo';
import { BILLING_TIERS, billingRecord, findBillingTier, formatGmd, formatUsd } from '../billingTiers';
import {
    MODULE_DEFS,
    OPTIONAL_RAIL_DEFS,
    defaultModules,
    defaultPayments,
    featuresFromTenant,
    gambiaPackEnabled,
    persistableFeatures,
    toggleOptionalRail,
    withGambiaPack
} from '../tenantFeatures';

const emptyCompanyForm = {
    companyName: '',
    slug: '',
    adminEmail: '',
    adminPassword: '',
    adminDisplayName: '',
    billingTier: 'standard',
    billingCycle: 'monthly',
    modules: defaultModules(),
    payments: defaultPayments()
};

function FeatureFlagsPanel({ modules, payments, onModules, onPayments, disabled }) {
    const packOn = gambiaPackEnabled(modules);
    const rails = (payments && payments.enabledRails) || ['cash'];
    return (
        <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <h6 className="mb-0">Modules & payment rails</h6>
                <label className="small mb-0">
                    <input
                        type="checkbox"
                        className="form-check-input me-1"
                        checked={packOn}
                        disabled={disabled}
                        onChange={(e) => onModules(withGambiaPack(modules, e.target.checked))}
                    />
                    Enable Gambia pack
                </label>
            </div>
            <p className="small text-muted mb-2">
                Optional add-ons. New companies start cash-only. Pulse Insurance demo keeps every module and rail on.
                Cash at the office is always available. Company admins set pay-to numbers only for rails you enable.
            </p>
            {MODULE_DEFS.map((def) => (
                <label key={def.key} className="d-block small mb-1">
                    <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={!!modules[def.key]}
                        disabled={disabled}
                        onChange={(e) => onModules({ ...modules, [def.key]: e.target.checked })}
                    />
                    {def.label}
                    {def.defaultOn ? <span className="text-muted"> (default on)</span> : null}
                </label>
            ))}
            <div className="mt-2 mb-1 small fw-semibold">Payment rails</div>
            <label className="d-block small mb-1 text-muted">
                <input type="checkbox" className="form-check-input me-2" checked disabled />
                Cash at the office (always on, default)
            </label>
            {OPTIONAL_RAIL_DEFS.map((rail) => (
                <label key={rail.id} className="d-block small mb-1">
                    <input
                        type="checkbox"
                        className="form-check-input me-2"
                        checked={rails.includes(rail.id)}
                        disabled={disabled}
                        onChange={(e) => onPayments(toggleOptionalRail(payments, rail.id, e.target.checked))}
                    />
                    {rail.label}
                </label>
            ))}
        </div>
    );
}

function SuperAdminDashboard() {
    const { session, loading } = useSuperAdminSession();
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [selected, setSelected] = useState(null);
    const [users, setUsers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
    const [userForm, setUserForm] = useState({ email: '', password: '', displayName: '', role: 'broker', department: '', branch: 'Head Office' });
    const [editTier, setEditTier] = useState('standard');
    const [editCycle, setEditCycle] = useState('monthly');
    const [editModules, setEditModules] = useState(defaultModules());
    const [editPayments, setEditPayments] = useState(defaultPayments());
    const [showDeleted, setShowDeleted] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmInput, setConfirmInput] = useState('');
    const [expandedUserId, setExpandedUserId] = useState(null);
    const [confirmDeleteUid, setConfirmDeleteUid] = useState(null);

    const previewTier = findBillingTier(companyForm.billingTier);
    const previewYearly = companyForm.billingCycle === 'yearly';

    const loadCompanies = async () => {
        const snap = await getDocs(collection(db, 'tenants'));
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
        setCompanies(list.filter((c) => c.status !== 'internal'));
        return list;
    };

    const loadUsers = async (tenantId) => {
        const snap = await getDocs(collection(db, 'tenants', tenantId, 'users'));
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        setUsers(list);
    };

    useEffect(() => {
        if (!session) return;
        loadCompanies().catch((err) => setMessage(err.message));
    }, [session]);

    const persistBilling = async (tenantId, tierId, cycle) => {
        const rec = billingRecord(tierId, cycle, session.email);
        await setDoc(doc(db, 'tenants', tenantId), { billing: rec }, { merge: true });
        await setDoc(doc(db, 'directory', tenantId), {
            billingTier: rec.tier,
            billingTierName: rec.tierName,
            billingCycle: rec.cycle,
            billingPriceUsd: rec.priceUsd
        }, { merge: true });
        return rec;
    };

    const persistLocale = async (tenantId, locale) => {
        const loc = locale === 'fr' ? 'fr' : 'en';
        await setDoc(doc(db, 'tenants', tenantId), { locale: loc }, { merge: true });
        await setDoc(doc(db, 'directory', tenantId), { locale: loc }, { merge: true });
        return loc;
    };

    const persistFeatures = async (tenantId, modules, payments) => {
        const rec = persistableFeatures(modules, payments);
        await setDoc(doc(db, 'tenants', tenantId), rec, { merge: true });
        await setDoc(doc(db, 'directory', tenantId), rec, { merge: true });
        return rec;
    };

    const handleSaveLocale = async (locale) => {
        if (!selected) return;
        setBusy(true);
        setMessage('');
        try {
            const loc = await persistLocale(selected.id, locale);
            const lang = loc === 'fr' ? 'Français' : 'English';
            setMessage('Language set to ' + lang + ' for ' + selected.companyName + '. Company staff and clients see it after they reload.');
            const list = await loadCompanies();
            const updated = list.find((c) => c.id === selected.id);
            if (updated) await selectCompany(updated);
        } catch (err) {
            setMessage(err.message || 'Could not update language');
        }
        setBusy(false);
    };

    const persistAccountStatus = async (tenantId, status) => {
        const now = new Date().toISOString();
        await setDoc(doc(db, 'tenants', tenantId), {
            status,
            statusUpdatedAt: now,
            statusUpdatedBy: session.email
        }, { merge: true });
        await setDoc(doc(db, 'directory', tenantId), { status }, { merge: true });
    };

    const applyAccountStatus = async (tenantId, status) => {
        await persistAccountStatus(tenantId, status);
        const fn = status === 'suspended' ? 'suspendCompany' : status === 'deleted' ? 'deleteCompany' : 'restoreCompany';
        callFunction(fn, { tenantId }).catch(() => {});
    };

    const matchesConfirm = (company, typed) => {
        const value = String(typed || '').trim().toLowerCase();
        if (!value) return false;
        return value === String(company.companyName || '').trim().toLowerCase()
            || value === String(company.slug || company.id || '').trim().toLowerCase();
    };

    const handleAccountAction = async () => {
        if (!confirmAction) return;
        const company = confirmAction.company;
        if (confirmAction.type === 'delete' && !matchesConfirm(company, confirmInput)) {
            setMessage('Type the company name or tenant id to confirm closure.');
            return;
        }
        setBusy(true);
        setMessage('');
        try {
            const status = confirmAction.type === 'suspend' ? 'suspended' : confirmAction.type === 'delete' ? 'deleted' : 'active';
            await applyAccountStatus(company.id, status);
            setConfirmAction(null);
            setConfirmInput('');
            const list = await loadCompanies();
            const updated = list.find((c) => c.id === company.id);
            if (updated) await selectCompany(updated);
            else setSelected(null);
            if (status === 'suspended') setMessage(company.companyName + ' is suspended. Staff cannot sign in. The insurance book was not opened or wiped.');
            else if (status === 'deleted') setMessage(company.companyName + ' is closed (soft delete). Staff logins are blocked. The insurance book was not bulk-deleted.');
            else setMessage(company.companyName + ' is active again. Staff can sign in.');
        } catch (err) {
            setMessage(err.message || 'Could not update account status');
        }
        setBusy(false);
    };

    const selectCompany = async (company) => {
        setSelected(company);
        setMessage('');
        setExpandedUserId(null);
        setConfirmDeleteUid(null);
        setEditTier(company.billing?.tier || 'standard');
        setEditCycle(company.billing?.cycle || 'monthly');
        const feats = featuresFromTenant(company, company.id);
        setEditModules(feats.modules);
        setEditPayments(feats.payments);
        await loadUsers(company.id);
    };

    const adminCount = users.filter((u) => String(u.role || '').toLowerCase() === 'admin').length;
    const isLastAdmin = (user) => String(user.role || '').toLowerCase() === 'admin' && adminCount <= 1;
    const isUserDisabled = (user) => user.disabled === true;

    const handleSetUserDisabled = async (user, disabled) => {
        if (!selected) return;
        setBusy(true);
        setMessage('');
        try {
            await callFunction('setTenantUserDisabled', { tenantId: selected.id, uid: user.id, disabled });
            setMessage((user.email || user.displayName || user.id) + (disabled ? ' is disabled and cannot sign in.' : ' is enabled and can sign in.'));
            await loadUsers(selected.id);
        } catch (err) {
            setMessage(err.message || 'Could not update staff login');
        }
        setBusy(false);
    };

    const handleDeleteUser = async (user) => {
        if (!selected) return;
        setBusy(true);
        setMessage('');
        try {
            await callFunction('deleteTenantUser', { tenantId: selected.id, uid: user.id });
            setMessage((user.email || user.displayName || user.id) + ' was deleted.');
            setConfirmDeleteUid(null);
            setExpandedUserId(null);
            await loadUsers(selected.id);
        } catch (err) {
            setMessage(err.message || 'Could not delete staff user');
        }
        setBusy(false);
    };

    const toggleUserRow = (uid) => {
        setExpandedUserId((current) => current === uid ? null : uid);
        setConfirmDeleteUid(null);
    };

    const handleCreateCompany = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMessage('');
        try {
            const result = await callFunction('createCompany', companyForm);
            const tenantId = result.tenantId;
            const rec = await persistBilling(tenantId, companyForm.billingTier, companyForm.billingCycle);
            await persistFeatures(tenantId, companyForm.modules, companyForm.payments);
            setMessage(
                'Created ' + (result.companyName || companyForm.companyName) +
                ' on ' + rec.tierName + ' (' + formatUsd(rec.priceUsd) +
                (rec.cycle === 'yearly' ? '/yr' : '/mo') + ', ' + rec.includedSeats + ' seats). ' +
                'Staff use /login (tenant from email). No public branded /c/ page on this hosting. Company IT admin issues broker API keys at /api-keys.'
            );
            setCompanyForm(emptyCompanyForm);
            const list = await loadCompanies();
            const created = list.find((c) => c.id === tenantId);
            if (created) await selectCompany(created);
        } catch (err) {
            setMessage(err.message || 'Failed to create company');
        }
        setBusy(false);
    };

    const handleSaveFeatures = async (e) => {
        e.preventDefault();
        if (!selected) return;
        setBusy(true);
        setMessage('');
        try {
            await persistFeatures(selected.id, editModules, editPayments);
            setMessage('Modules and payment rails saved for ' + selected.companyName + '. Staff see the change after they reload.');
            const list = await loadCompanies();
            const updated = list.find((c) => c.id === selected.id);
            if (updated) await selectCompany(updated);
        } catch (err) {
            setMessage(err.message || 'Could not update modules');
        }
        setBusy(false);
    };

    const handleSavePackage = async (e) => {
        e.preventDefault();
        if (!selected) return;
        setBusy(true);
        setMessage('');
        try {
            const rec = await persistBilling(selected.id, editTier, editCycle);
            setMessage(selected.companyName + ' is now on ' + rec.tierName + ' (' + formatUsd(rec.priceUsd) + (rec.cycle === 'yearly' ? '/yr' : '/mo') + ').');
            const list = await loadCompanies();
            const updated = list.find((c) => c.id === selected.id);
            if (updated) await selectCompany(updated);
        } catch (err) {
            setMessage(err.message || 'Could not update package');
        }
        setBusy(false);
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!selected) return;
        setBusy(true);
        setMessage('');
        try {
            await callFunction('createTenantUser', { ...userForm, tenantId: selected.id });
            setMessage('Granted access to ' + userForm.email);
            setUserForm({ email: '', password: '', displayName: '', role: 'broker', department: '', branch: 'Head Office' });
            await loadUsers(selected.id);
        } catch (err) {
            setMessage(err.message || 'Failed to create user');
        }
        setBusy(false);
    };

    if (loading || !session) return <div className="p-5">Loading Pulse admin...</div>;

    const billingLabel = (c) => {
        const b = c.billing;
        if (!b?.tierName) return 'No package set';
        return b.tierName + ' · ' + formatGmd(b.priceUsd) + ' (' + formatUsd(b.priceUsd) + ')' + (b.cycle === 'yearly' ? '/yr' : '/mo');
    };

    return (
        <div className="container-fluid p-0" style={{ background: '#F1F5F9', minHeight: '100vh' }}>
            <nav className="navbar px-4" style={{ backgroundColor: '#0F172A' }}>
                <Link to="/super-admin" className="navbar-brand mb-0"><PulseLogo variant="dark" showTagline={false} /></Link>
                <div className="ms-auto d-flex align-items-center gap-2">
                    <Link to="/verify" className="btn btn-outline-warning btn-sm">USSD / police verify</Link>
                    <span className="badge bg-warning text-dark">SUPER ADMIN</span>
                    <span className="text-white-50 small">{session.email}</span>
                    <button className="btn btn-outline-light btn-sm" onClick={async () => { await signOut(auth); navigate('/super-admin/login'); }}>Logout</button>
                </div>
            </nav>
            <div className="container py-4">
                <h4 className="mb-1">Platform administration</h4>
                <p className="text-muted">Pulse super admin manages tenants, company users, and licence/billing only — not the insurance book (customers, policies, claims). Suspend or close an account if agreements are violated.</p>
                {message && <div className="alert alert-info">{message}</div>}
                <div className="row g-4">
                    <div className="col-lg-5">
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-body">
                                <h6>Onboard insurance company</h6>
                                <form onSubmit={handleCreateCompany}>
                                    <input className="form-control mb-2" placeholder="Company name" value={companyForm.companyName} onChange={(e) => setCompanyForm({ ...companyForm, companyName: e.target.value })} required />
                                    <input className="form-control mb-2" placeholder="Internal tenant id (optional)" value={companyForm.slug} onChange={(e) => setCompanyForm({ ...companyForm, slug: e.target.value })} />
                                    <p className="small text-muted mb-2">Internal id only. Staff sign in at <code>/login</code> (tenant from email claims). This site does not publish a public branded page at <code>{'/c/{id}'}</code> for other insurers — only Pulse Insurance.</p>
                                    <input className="form-control mb-2" placeholder="Company admin name" value={companyForm.adminDisplayName} onChange={(e) => setCompanyForm({ ...companyForm, adminDisplayName: e.target.value })} required />
                                    <input type="email" className="form-control mb-2" placeholder="Company admin email" value={companyForm.adminEmail} onChange={(e) => setCompanyForm({ ...companyForm, adminEmail: e.target.value })} required />
                                    <input type="password" className="form-control mb-2" placeholder="Temporary password" value={companyForm.adminPassword} onChange={(e) => setCompanyForm({ ...companyForm, adminPassword: e.target.value })} required minLength={8} />

                                    <label className="form-label small fw-semibold mb-1 mt-2">Licence package</label>
                                    <div className="mb-2">
                                        {BILLING_TIERS.map((t) => (
                                            <label key={t.id} className={'d-block border rounded p-2 mb-2' + (companyForm.billingTier === t.id ? ' border-dark bg-light' : '')} style={{ cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    className="form-check-input me-2"
                                                    name="billingTier"
                                                    checked={companyForm.billingTier === t.id}
                                                    onChange={() => setCompanyForm({ ...companyForm, billingTier: t.id })}
                                                />
                                                <span className="fw-semibold">{t.name}</span>
                                                <span className="text-muted small ms-2">
                                                    {formatGmd(previewYearly ? t.yearlyUsd : t.monthlyUsd)}
                                                    {previewYearly ? '/yr' : '/mo'}
                                                    {' · '}{formatUsd(previewYearly ? t.yearlyUsd : t.monthlyUsd)}
                                                    {' · '}{t.includedSeats} seats
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                    <select
                                        className="form-select mb-2"
                                        value={companyForm.billingCycle}
                                        onChange={(e) => setCompanyForm({ ...companyForm, billingCycle: e.target.value })}
                                    >
                                        <option value="monthly">Billed monthly</option>
                                        <option value="yearly">Billed yearly (2 months free)</option>
                                    </select>
                                    <p className="small text-muted mb-3">
                                        {previewTier.blurb} Setup {formatGmd(previewTier.setupUsd)} ({formatUsd(previewTier.setupUsd)}). Extra seats {formatGmd(previewTier.extraSeatUsd)} each.
                                    </p>
                                    <div className="border rounded p-3 mb-3 bg-light">
                                        <FeatureFlagsPanel
                                            modules={companyForm.modules || defaultModules()}
                                            payments={companyForm.payments || defaultPayments()}
                                            onModules={(modules) => setCompanyForm({ ...companyForm, modules })}
                                            onPayments={(payments) => setCompanyForm({ ...companyForm, payments })}
                                            disabled={busy}
                                        />
                                    </div>
                                    <button className="btn btn-dark w-100" disabled={busy}>{busy ? 'Working...' : 'Create company + admin'}</button>
                                </form>
                            </div>
                        </div>
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0">Insurance companies</h6>
                                    <label className="small text-muted mb-0">
                                        <input type="checkbox" className="form-check-input me-1" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
                                        Show closed
                                    </label>
                                </div>
                                <div className="list-group">
                                    {companies.filter((c) => showDeleted || c.status !== 'deleted').map((c) => (
                                        <button key={c.id} type="button" className={'list-group-item list-group-item-action' + (selected?.id === c.id ? ' active' : '')} onClick={() => selectCompany(c)}>
                                            <div className="fw-bold">
                                                {c.companyName || c.id}
                                                {c.status === 'suspended' && <span className="badge bg-warning text-dark ms-2">Suspended</span>}
                                                {c.status === 'deleted' && <span className="badge bg-danger ms-2">Closed</span>}
                                                <span className="badge bg-light text-dark border ms-2">{(c.locale === 'fr' ? 'FR' : 'EN')}</span>
                                            </div>
                                            <small>{c.adminEmail || c.id} · {billingLabel(c)}</small>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-7">
                        {!selected ? (
                            <div className="card border-0 shadow-sm"><div className="card-body text-muted">Select a company to set their package or grant department access.</div></div>
                        ) : (
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <h6 className="mb-1">
                                        {selected.companyName}
                                        {selected.status === 'suspended' && <span className="badge bg-warning text-dark ms-2">Suspended</span>}
                                        {selected.status === 'deleted' && <span className="badge bg-danger ms-2">Closed</span>}
                                    </h6>
                                    <p className="text-muted small mb-3">
                                        Staff: <code>/login</code> (tenant from email). This hosting has no public branded <code>{'/c/{slug}'}</code> for this company. Broker API: <code>/api/v1/{selected.id}</code>.
                                        {' '}Seats used: {users.length}
                                        {selected.billing?.includedSeats ? ' / ' + selected.billing.includedSeats : ''}.
                                    </p>

                                    <div className="border rounded p-3 mb-4">
                                        <h6 className="mb-1">Language / Langue</h6>
                                        <p className="small text-muted mb-2">Staff, client portal, and police verification for this company. Super admin portal stays in English. Company admins cannot change this.</p>
                                        <div className="btn-group" role="group" aria-label="Tenant language">
                                            <button
                                                type="button"
                                                className={'btn ' + ((selected.locale || 'en') !== 'fr' ? 'btn-dark' : 'btn-outline-dark')}
                                                disabled={busy}
                                                onClick={() => handleSaveLocale('en')}
                                            >
                                                English
                                            </button>
                                            <button
                                                type="button"
                                                className={'btn ' + (selected.locale === 'fr' ? 'btn-dark' : 'btn-outline-dark')}
                                                disabled={busy}
                                                onClick={() => handleSaveLocale('fr')}
                                            >
                                                Français
                                            </button>
                                        </div>
                                    </div>

                                    <form className="border rounded p-3 mb-4" onSubmit={handleSaveFeatures}>
                                        <FeatureFlagsPanel
                                            modules={editModules}
                                            payments={editPayments}
                                            onModules={setEditModules}
                                            onPayments={setEditPayments}
                                            disabled={busy}
                                        />
                                        <button className="btn btn-outline-dark mt-3" disabled={busy}>Save modules & rails</button>
                                    </form>

                                    <form className="border rounded p-3 mb-4 bg-light" onSubmit={handleSavePackage}>
                                        <h6 className="mb-2">Licence package</h6>
                                        <div className="row g-2 align-items-end">
                                            <div className="col-md-5">
                                                <select className="form-select" value={editTier} onChange={(e) => setEditTier(e.target.value)}>
                                                    {BILLING_TIERS.map((t) => (
                                                        <option key={t.id} value={t.id}>{t.name} — {formatGmd(editCycle === 'yearly' ? t.yearlyUsd : t.monthlyUsd)} ({formatUsd(editCycle === 'yearly' ? t.yearlyUsd : t.monthlyUsd)}){editCycle === 'yearly' ? '/yr' : '/mo'}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <select className="form-select" value={editCycle} onChange={(e) => setEditCycle(e.target.value)}>
                                                    <option value="monthly">Monthly</option>
                                                    <option value="yearly">Yearly</option>
                                                </select>
                                            </div>
                                            <div className="col-md-3">
                                                <button className="btn btn-outline-dark w-100" disabled={busy}>Save package</button>
                                            </div>
                                        </div>
                                        {selected.billing?.tierName && (
                                            <p className="small text-muted mb-0 mt-2">
                                                Current: {selected.billing.tierName} · {formatGmd(selected.billing.priceUsd)} ({formatUsd(selected.billing.priceUsd)}){selected.billing.cycle === 'yearly' ? '/yr' : '/mo'} · {selected.billing.includedSeats} seats included · extra {formatGmd(selected.billing.extraSeatUsd)} · setup {formatGmd(selected.billing.setupUsd)}
                                            </p>
                                        )}
                                    </form>

                                    <form className="row g-2 mb-4" onSubmit={handleCreateUser}>
                                        <div className="col-md-6"><input className="form-control" placeholder="Display name" value={userForm.displayName} onChange={(e) => setUserForm({ ...userForm, displayName: e.target.value })} required /></div>
                                        <div className="col-md-6"><input type="email" className="form-control" placeholder="Email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} required /></div>
                                        <div className="col-md-4"><input type="password" className="form-control" placeholder="Password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required minLength={8} /></div>
                                        <div className="col-md-4">
                                            <select className="form-select" value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                                                <option value="admin">Company admin</option>
                                                <option value="manager">Manager</option>
                                                <option value="underwriter">Underwriter</option>
                                                <option value="broker">Broker</option>
                                                <option value="claims">Claims</option>
                                                <option value="finance">Finance</option>
                                                <option value="audit">Audit</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4"><input className="form-control" placeholder="Department" value={userForm.department} onChange={(e) => setUserForm({ ...userForm, department: e.target.value })} /></div>
                                        <div className="col-12"><button className="btn btn-primary" disabled={busy}>Grant department access</button></div>
                                    </form>
                                    <table className="table table-sm align-middle">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Email</th>
                                                <th>Role</th>
                                                <th>Auth</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((u) => {
                                                const expanded = expandedUserId === u.id;
                                                const disabled = isUserDisabled(u);
                                                return (
                                                    <React.Fragment key={u.id}>
                                                        <tr
                                                            onClick={() => toggleUserRow(u.id)}
                                                            style={{ cursor: 'pointer' }}
                                                            className={expanded ? 'table-active' : ''}
                                                        >
                                                            <td>
                                                                {u.displayName || '—'}
                                                                {disabled && <span className="badge bg-secondary ms-2">Disabled</span>}
                                                            </td>
                                                            <td>{u.email}</td>
                                                            <td>{u.role}</td>
                                                            <td>{disabled ? 'Disabled' : 'Enabled'}</td>
                                                        </tr>
                                                        {expanded && (
                                                            <tr className="table-light">
                                                                <td colSpan={4}>
                                                                    <div className="small mb-2">
                                                                        <div><strong>Email:</strong> {u.email || '—'}</div>
                                                                        <div><strong>Role:</strong> {u.role || '—'}</div>
                                                                        <div><strong>Auth disabled:</strong> {disabled ? 'Yes — this login cannot sign in' : 'No — login is enabled'}</div>
                                                                    </div>
                                                                    {confirmDeleteUid === u.id ? (
                                                                        <div className="alert alert-danger mb-0 py-2">
                                                                            <p className="mb-2">
                                                                                Delete <strong>{u.email || u.displayName}</strong>? This removes the Firebase Auth user and their company staff record. The insurance book is not touched.
                                                                            </p>
                                                                            {isLastAdmin(u) && (
                                                                                <p className="mb-2 fw-semibold">This is the last remaining company admin. Deleting them leaves this tenant with no IT admin.</p>
                                                                            )}
                                                                            <button className="btn btn-danger btn-sm me-2" type="button" disabled={busy} onClick={(e) => { e.stopPropagation(); handleDeleteUser(u); }}>Confirm delete</button>
                                                                            <button className="btn btn-outline-secondary btn-sm" type="button" disabled={busy} onClick={(e) => { e.stopPropagation(); setConfirmDeleteUid(null); }}>Cancel</button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="d-flex flex-wrap gap-2">
                                                                            <button className="btn btn-outline-warning btn-sm" type="button" disabled={busy || disabled} onClick={(e) => { e.stopPropagation(); handleSetUserDisabled(u, true); }}>Disable</button>
                                                                            <button className="btn btn-outline-success btn-sm" type="button" disabled={busy || !disabled} onClick={(e) => { e.stopPropagation(); handleSetUserDisabled(u, false); }}>Enable</button>
                                                                            <button className="btn btn-outline-danger btn-sm" type="button" disabled={busy} onClick={(e) => { e.stopPropagation(); setConfirmDeleteUid(u.id); }}>Delete</button>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                            {!users.length && (
                                                <tr><td colSpan={4} className="text-muted">No staff users on this company yet.</td></tr>
                                            )}
                                        </tbody>
                                    </table>

                                    <div className="border rounded p-3 mt-2">
                                        <h6 className="mb-2">Account controls</h6>
                                        <p className="small text-muted mb-3">If agreements are violated, suspend or close this <em>account</em>. Pulse does not open or bulk-delete the insurance book from this portal.</p>
                                        {confirmAction && confirmAction.company.id === selected.id ? (
                                            <div className="alert alert-warning mb-0">
                                                {confirmAction.type === 'suspend' && (
                                                    <>
                                                        <p className="mb-2">Suspend <strong>{selected.companyName}</strong>? Staff will be blocked from login. Directory will show Suspended.</p>
                                                        <button className="btn btn-warning me-2" disabled={busy} type="button" onClick={handleAccountAction}>Confirm suspend</button>
                                                    </>
                                                )}
                                                {confirmAction.type === 'delete' && (
                                                    <>
                                                        <p className="mb-2">Close <strong>{selected.companyName}</strong> (soft delete)? Type the company name or tenant id (<code>{selected.slug || selected.id}</code>) to confirm. The book is not bulk-deleted.</p>
                                                        <input className="form-control mb-2" placeholder="Company name or tenant id" value={confirmInput} onChange={(e) => setConfirmInput(e.target.value)} />
                                                        <button className="btn btn-danger me-2" disabled={busy || !matchesConfirm(selected, confirmInput)} type="button" onClick={handleAccountAction}>Confirm close account</button>
                                                    </>
                                                )}
                                                {confirmAction.type === 'restore' && (
                                                    <>
                                                        <p className="mb-2">Re-activate <strong>{selected.companyName}</strong> so staff can sign in again?</p>
                                                        <button className="btn btn-success me-2" disabled={busy} type="button" onClick={handleAccountAction}>Confirm restore</button>
                                                    </>
                                                )}
                                                <button className="btn btn-outline-secondary" type="button" disabled={busy} onClick={() => { setConfirmAction(null); setConfirmInput(''); }}>Cancel</button>
                                            </div>
                                        ) : (
                                            <div className="d-flex flex-wrap gap-2">
                                                {selected.status !== 'suspended' && selected.status !== 'deleted' && (
                                                    <button className="btn btn-outline-warning" type="button" disabled={busy} onClick={() => { setConfirmAction({ type: 'suspend', company: selected }); setConfirmInput(''); }}>Suspend account</button>
                                                )}
                                                {selected.status !== 'deleted' && (
                                                    <button className="btn btn-outline-danger" type="button" disabled={busy} onClick={() => { setConfirmAction({ type: 'delete', company: selected }); setConfirmInput(''); }}>Delete account</button>
                                                )}
                                                {(selected.status === 'suspended' || selected.status === 'deleted') && (
                                                    <button className="btn btn-outline-success" type="button" disabled={busy} onClick={() => { setConfirmAction({ type: 'restore', company: selected }); setConfirmInput(''); }}>Restore account</button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SuperAdminDashboard;
